import {type FC, memo} from 'react';
import {buildStorageBlurUrl, buildStorageTransformUrl, type OutputFormat, type ResizeMode} from './storageImageUrl';

/**
 * Компонент для изображений из storage с контролем над брейкпоинтами,
 * трансформациями и поддержкой DPR (1x/2x/3x). Рендерит <picture> с <source>.
 */

interface SourceDescriptor {
  media: string;
  width: number;
  height?: number;
  resize?: ResizeMode;
  format?: OutputFormat;
  quality?: number;
  dprs?: number[];
}

interface StorageImageProps {
  src: string;
  alt: string;
  sources: SourceDescriptor[];
  className?: string;
  quality?: number;
  withBlur?: boolean;
  height?: number;
  resize?: ResizeMode;
  format?: OutputFormat;
  dprs?: number[];
  onError?: () => void;
}

const buildDprSrcSet = (
  src: string,
  baseWidth: number,
  baseQuality: number,
  dprs: number[],
  perSource: {height?: number; resize?: ResizeMode; format?: OutputFormat; quality?: number},
  globalDefaults: {height?: number; resize?: ResizeMode; format?: OutputFormat},
): string => {
  const effectiveQuality = perSource.quality ?? baseQuality;
  const height = perSource.height ?? globalDefaults.height;
  const resize = perSource.resize ?? globalDefaults.resize;
  const format = perSource.format ?? globalDefaults.format;

  const parts = dprs.map(dpr => {
    const width = Math.max(1, Math.round(baseWidth * dpr));
    const h = typeof height === 'number' ? Math.max(1, Math.round(height * dpr)) : undefined;
    const url = buildStorageTransformUrl(src, {
      width,
      height: h,
      quality: effectiveQuality,
      resize,
      format,
    });
    return `${url} ${dpr}x`;
  });

  return parts.join(', ');
};

export const StorageImage: FC<StorageImageProps> = memo(
  ({src, alt, sources, className, quality = 75, withBlur = false, height, resize, format, dprs, onError}) => {
    const fallback = sources[sources.length - 1];
    const blur = withBlur ? buildStorageBlurUrl(src) : undefined;
    const globalDprs = (dprs && dprs.length > 0 ? dprs : [1, 2]) as number[];

    return (
      <picture>
        {sources.map(({media, width, height: h, resize: r, format: f, quality: q, dprs: localDprs}) => (
          <source
            key={media}
            media={media}
            onError={onError}
            srcSet={buildDprSrcSet(
              src,
              width,
              quality,
              localDprs && localDprs.length > 0 ? localDprs : globalDprs,
              {height: h, resize: r, format: f, quality: q},
              {height, resize, format},
            )}
          />
        ))}
        <img
          onError={onError}
          src={buildStorageTransformUrl(src, {
            width: fallback.width,
            height: fallback.height ?? height,
            quality: fallback.quality ?? quality,
            resize: fallback.resize ?? resize,
            format: fallback.format ?? format,
          })}
          alt={alt}
          className={className}
          loading="lazy"
          style={
            withBlur
              ? {
                  backgroundImage: `url(${blur})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        />
      </picture>
    );
  },
);

StorageImage.displayName = 'StorageImage';

export type {StorageImageProps, SourceDescriptor};
